import {Injectable, Optional} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {ApiConfig} from '../../../../../api-endpoint/src/lib/api.config';
import {Auth} from '@kion/data-models';

interface IResponse {
  access_token: string,
  scope: string,
  expires_in: number,
  refresh_token: string,
  idp_language: string
}

@Injectable({
  providedIn: 'root'
})
export class AuthConnectors {

  private readonly _rootUrl: string;
  private readonly _baseSite: string;
  private readonly clientId: string;
  private readonly clientSecret: string;

  private route = '/authorizationserver/oauth/';

  constructor(
    @Optional() apiConfig: ApiConfig,
    private http: HttpClient,
  ) {
    this._rootUrl = apiConfig.rootUrl;
    this.clientId = apiConfig.clientId;
    this.clientSecret = apiConfig.clientSecret;
    this._baseSite = apiConfig.baseSite;
  }

  authenticateClient(): Observable<Partial<Auth>> {
    return this.http.post<IResponse>(this.getAuthUrl() + '?grant_type=client_credentials&client_secret=' + this.clientSecret + '&client_id=' + this.clientId, {}).pipe(
      map(
        (response: IResponse) => {
          return {
            client: {
              accessToken: response.access_token,
              scope: response.scope,
              expiresIn: response.expires_in
            }
          }
        }
      ),
      catchError(error => throwError(error))
    )
  }

  authenticate(username: string, password: string): Observable<Partial<Auth>> {
    return this.http.post<IResponse>(
      this.getAuthUrl() + '?grant_type=password&client_secret=' + this.clientSecret + '&client_id=' + this.clientId +
      '&username=' + encodeURIComponent(username) + '&password=' + encodeURIComponent(password),
      {}
    ).pipe(
      map(
        (response: IResponse) => {
          return {
            user: {
              accessToken: response.access_token,
              scope: response.scope,
              expiresIn: response.expires_in,
              refreshToken: response.refresh_token
            }
          }
        }
      ),
      catchError(error => throwError(error))
    )
  }

  tokenAuthentication(userName: string, idToken: string, accessToken: string, idp: string, oid: string): Observable<Partial<Auth>> {
    return this.http.post<IResponse>(
      this.getTokenAuthUrl() + '?grant_type=identity_token&client_secret=' + this.clientSecret + '&client_id=' + this.clientId +
      '&username=' + encodeURIComponent(userName) + '&identitytoken=' + encodeURIComponent(idToken) + '&accesstoken=' + encodeURIComponent(accessToken) +
      '&idpUserFlow=' + idp + '&idpUserId=' + oid,
      {}
    ).pipe(
      map(
        (response: IResponse) => {
          return {
            user: {
              accessToken: response.access_token,
              scope: response.scope,
              expiresIn: response.expires_in,
              refreshToken: response.refresh_token,
              idpLanguage: response.idp_language
            }
          }
        }
      ),
      catchError(error => throwError(error))
    )
  }

  tokenAuthenticationGlobal(userName: string, idToken: string, accessToken: string, idp: string, oid: string, unitNumber: string, hash: string, isGSMi: string, GSMiSite: string, dematicPunchOutKey: string, dematicPunchOutSid: string): Observable<Partial<Auth>> {
    return this.http.post<IResponse>(
      this.getTokenAuthGlobalUrl() + '?grant_type=auto_login&client_secret=' + this.clientSecret + '&client_id=' + this.clientId +
      '&username=' + encodeURIComponent(userName) + '&identitytoken=' + encodeURIComponent(idToken) + '&accesstoken=' + encodeURIComponent(accessToken) +
      '&idpUserFlow=' + idp + '&idpUserId=' + oid + '&unitNumber=' + unitNumber + '&hash=' + hash + '&isGSMi=' + isGSMi + '&GSMiSite=' + GSMiSite + '&dematicPunchOutKey=' + encodeURIComponent(dematicPunchOutKey) + '&dematicPunchOutSid=' + encodeURIComponent(dematicPunchOutSid),
      {}
    ).pipe(
      map(
        (response: IResponse) => {
          return {
            user: {
              accessToken: response.access_token,
              scope: response.scope,
              expiresIn: response.expires_in,
              refreshToken: response.refresh_token
            }
          }
        }
      ),
      catchError(error => throwError(error))
    )
  }

  checkWhoAmI(accessToken: string): Observable<any> {
    return this.http.get(this.getWhoAmiUrl() + '?access_token=' + accessToken).pipe(
      map(
        response => response
      ),
      catchError(error => throwError(error))
    )
  }

  refreshToken(refreshToken: string): Observable<Partial<Auth>> {
    return this.http.post(this.getAuthUrl() + '?grant_type=refresh_token&refresh_token=' + refreshToken +
      '&client_secret=' + this.clientSecret + '&client_id=' + this.clientId, {}).pipe(
      map(
        (response: IResponse) => {
          return {
            user: {
              accessToken: response.access_token,
              scope: response.scope,
              expiresIn: response.expires_in,
              refreshToken: response.refresh_token
            }
          }
        }
      ),
      catchError(error => throwError(error))
    )
  }

  protected getAuthUrl() {
    return this._rootUrl.split('/rest/v2')[0] + this.route + 'token';
  }

  protected getWhoAmiUrl(): string {
    return this._rootUrl.split('/rest/v2')[0] + this.route + 'whoami';
  }

  protected getRefreshUrl(): string {
    return this._rootUrl.split('/rest/v2')[0] + this.route + 'refresh';
  }

  protected getTokenAuthUrl(): string {
    return this._rootUrl + this._baseSite + '/login';
  }

  protected getTokenAuthGlobalUrl(): string {
    return this._rootUrl + this._baseSite + '/login/global';
  }
}
